import React, { useState, useEffect } from 'react';
import { useParams, Redirect } from 'react-router-dom';
import { getIncomeGain } from 'config/api';
import {
  IncomeGainProps,
  IncomeGainItemProps,
  getSummaryIncomeGainAmount,
} from 'pages/DividendDeposit';
import { formatNumber, formatDate } from 'utils/formatter';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import { DividendDetailList } from 'components/features/dividendDeposit/DividendClassification';
import {
  DividendDepositModal,
  getMonthArray,
} from 'components/features/dividendDeposit/DividendTransition';
import {
  DividendPieChart,
  DividendPieChartNoData,
} from 'components/features/dividendDeposit/DividendChart';
import { DividendDepositLoading } from 'components/features/dividendDeposit/DividendError';
import { yyyymmddToDate } from 'components/features/new-report/daily-report/DailyReport';
import { isIosAppNotPremium } from 'components/features/utils/userStatus';
import { DividendHeader, FooterText } from './DividendPrediction';
import { formatYM } from './DividendDetailPrediction';

export const getSortIncomeGainAmountData = (
  incomeGainData: IncomeGainItemProps[]
): IncomeGainItemProps[] => {
  const sortData = incomeGainData.sort((first, second) => {
    const firstAmount = first.amount ?? 0;
    const secondAmount = second.amount ?? 0;
    if (firstAmount < secondAmount) {
      return 1;
    } else if (firstAmount > secondAmount) {
      return -1;
    } else {
      return 0;
    }
  });

  return sortData;
};

// memo: 証券会社違いの同一銘柄をサマる
export const getSummaryIncomeGain = (
  data: IncomeGainItemProps[]
): IncomeGainItemProps[] => {
  const incomeGainList = Array.from(
    new Set(
      data.map(item => {
        return { symbol: item.symbol, symbolName: item.symbolName };
      })
    )
  );

  const uniqueArray = Array.from(
    new Map(incomeGainList.map(item => [item.symbolName, item])).values()
  );

  const targetIncomeGainList = uniqueArray.map(list => {
    return data.filter(
      data => data.symbol === list.symbol && data.symbolName === list.symbolName
    );
  });

  const summaryIncomeGainData = targetIncomeGainList
    .map(list => {
      const productTypeList = Array.from(
        new Set(list.map(item => item.productType))
      );

      const summaryIncomeGainData = productTypeList.map(productType => {
        const targetData = list.filter(
          list => list.productType === productType
        );

        const isAmountNull = targetData.every(data => data.amount === null);
        if (targetData.length === 1 || isAmountNull) return targetData[0];

        const summaryTargetData = targetData.reduce((acc, current, index) => {
          if (index === 0) return { ...current };

          const sumAmount = (acc.amount ?? 0) + (current.amount ?? 0);
          return { ...acc, amount: sumAmount };
        }, {} as IncomeGainItemProps);

        return summaryTargetData;
      }) as IncomeGainItemProps[];
      return summaryIncomeGainData as IncomeGainItemProps[];
    })
    .flat();

  const sortedSummaryIncomeGainData = getSortIncomeGainAmountData(
    summaryIncomeGainData
  );

  return sortedSummaryIncomeGainData;
};

const DividendDetailDeposit = (): React.ReactElement => {
  const { setWithBackIconWord } = useCustomizeHeaderContext();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(setWithBackIconWord, []);

  const { date, from, to } = useParams<{
    date: string;
    from: string;
    to: string;
  }>();

  const [incomeGainData, setIncomeGainData] = useState<
    IncomeGainItemProps[] | null
  >(null);
  const [displayIncomeGainData, setDisplayIncomeGainData] = useState<
    IncomeGainItemProps[] | null
  >(null);
  const [targetIncomeGainData, setTargetIncomeGainData] = useState<
    IncomeGainItemProps[] | null
  >(null);
  const [totalDividend, setTotalDividend] = useState<number | null>(null);

  const period = {
    from: yyyymmddToDate(from) ?? new Date(),
    to: yyyymmddToDate(to) ?? new Date(),
  };
  const dateStates = getMonthArray(period.from, period.to);
  const [selectedDate, setSelectedDate] = useState<string>(date);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getYm = (date: string): string => {
    const data = yyyymmddToDate(date);
    return formatDate(data, 'yyyyMM');
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      const queryFrom = getYm(from);
      const queryTo = getYm(to);

      const ret_ = await getIncomeGain(queryFrom, queryTo);
      const ret = {
        from: ret_.From,
        to: ret_.To,
        items: ret_.Items.map(item => {
          return {
            account: item.Account,
            brokerID: item.BrokerID,
            brokerName: item.BrokerName,
            symbol: item.Symbol,
            symbolName: item.SymbolName,
            productType: item.ProductType,
            amount: item.Amount,
            date: item.Date,
            isin: item.ISIN,
            hasNew: item.HasNew,
          };
        }) as IncomeGainItemProps[],
      } as IncomeGainProps;
      setIncomeGainData(ret.items);

      const targetIncomeGainData = ret.items.filter(data => {
        const dateYm = getYm(data.date);
        return dateYm === date;
      });

      const summaryTargetIncomeGainData = getSummaryIncomeGain(
        targetIncomeGainData
      );

      const sortedTargetIncomeGainData = getSortIncomeGainAmountData(
        summaryTargetIncomeGainData
      );
      setDisplayIncomeGainData(sortedTargetIncomeGainData);

      const totalDividend = getSummaryIncomeGainAmount(
        sortedTargetIncomeGainData
      );
      setTotalDividend(totalDividend);
      setIsLoading(false);

      const targetIncomeGainData_ = ret.items.filter(item => {
        const targetDate_ = yyyymmddToDate(item.date);
        if (targetDate_ === null) return false;
        const targetDate = formatDate(targetDate_, 'yyyyMM');
        return targetDate === selectedDate;
      });
      setTargetIncomeGainData(targetIncomeGainData_);
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (incomeGainData === null) return;

    const targetIncomeGainData = incomeGainData.filter(data => {
      const dateYm = getYm(data.date);
      return dateYm === selectedDate;
    });

    const summaryTargetIncomeGainData = getSummaryIncomeGain(
      targetIncomeGainData
    );

    const sortedTargetIncomeGainData = getSortIncomeGainAmountData(
      summaryTargetIncomeGainData
    );
    setDisplayIncomeGainData(sortedTargetIncomeGainData);

    const totalDividend = getSummaryIncomeGainAmount(
      sortedTargetIncomeGainData
    );
    setTotalDividend(totalDividend);

    const targetIncomeGainData_ = incomeGainData.filter(item => {
      const targetDate_ = yyyymmddToDate(item.date);
      if (targetDate_ === null) return false;
      const targetDate = formatDate(targetDate_, 'yyyyMM');
      return targetDate === selectedDate;
    });
    setTargetIncomeGainData(targetIncomeGainData_);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  if (isIosAppNotPremium()) return <Redirect to="/home" />;

  if (
    incomeGainData === null ||
    displayIncomeGainData === null ||
    targetIncomeGainData === null ||
    totalDividend === null ||
    isLoading
  )
    return <DividendDepositLoading str={'銘柄'} />;

  return (
    <>
      <DividendHeader {...{ type: 'deposit' }} />
      <div className="bg-white text-bluegray-700">
        <div
          className="relative w-full flex flex-row justify-center items-center mx-auto pt-6"
          style={{ width: '135px' }}>
          <select
            className={`h-full w-full pl-2 appearance-none bg-transparent text-base font-bold cursor-pointer`}
            value={selectedDate}
            onChange={(e): void => {
              setSelectedDate(e.currentTarget.value);
            }}>
            {dateStates.map((s, i) => {
              return (
                <option key={i} value={s.toString()}>
                  {formatYM(s)}
                </option>
              );
            })}
          </select>
          <div
            className={`absolute icon-arrow_drop_down pointer-events-none p-0.5 text-2xl`}
            style={{ right: '6%' }}></div>
        </div>
        <div className="px-4 pt-2 text-center text-xs">
          <span className="mr-1 text-2xl font-bold">
            {formatNumber(totalDividend, {})}
          </span>
          円
        </div>

        <div className="my-2 mx-1">
          {displayIncomeGainData.length === 0 ? (
            <DividendPieChartNoData />
          ) : (
            <DividendPieChart {...{ incomeGainData: displayIncomeGainData }} />
          )}
        </div>
        <DividendDepositModal />
        <DividendDetailList
          {...{
            displayIncomeGainData,
            incomeGainData: targetIncomeGainData,
            totalDividend,
          }}
        />
      </div>
      <FooterText />
    </>
  );
};

export default DividendDetailDeposit;
